import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { STATUS_META, type AppointmentStatus } from '@/api/appointments.api';
import { settingsApi } from '@/api/settings.api';
import { useSession } from '@/app/session';
import { STATUS_COLOR } from '@/lib/status-colors';

/**
 * The appointment statuses, as this clinic words them.
 *
 * The set of statuses is the application's, not the clinic's: the whole
 * system branches on `done`, `noshow`, `cancelled`, `pending` — the status
 * lock, the payment flow, the 043/e gate, every report. What a clinic may
 * change is what each one is *called*, which mark stands for it and which
 * colour it is drawn in, because that is presentation and nothing reads it.
 *
 * The reference system also offers "status nou" and a delete. It should not:
 * its own calendar falls back to `confirmed` for a status it does not know,
 * and its payment and no-show logic keys off the built-in names — so a custom
 * status looks distinct on the grid and behaves as "confirmed" everywhere it
 * matters. That is a trap, not a feature; see finding R28.
 */
export interface StatusLook {
  label: string;
  icon: string;
  tone: (typeof STATUS_META)[AppointmentStatus]['tone'];
  /**
   * Culoarea statusului pe calendar: fâșia din stânga cardului, conturul în
   * stilurile mari, punctul din legendă și din tooltip. Implicit un token din
   * `tokens.css`, care își schimbă nuanța în tema întunecată; aleasă de clinică,
   * un hex, același în ambele teme.
   */
  color: string;
}

export type StatusMeta = Record<AppointmentStatus, StatusLook>;

/** What the clinic has overridden, by status key. */
export type StatusOverrides = Partial<
  Record<AppointmentStatus, { label?: string; icon?: string; color?: string }>
>;

/** Doar un hex complet: o valoare stricată în configurație nu vopsește calendarul. */
const HEX = /^#[0-9a-f]{6}$/i;

export function mergeStatusMeta(overrides: StatusOverrides | undefined): StatusMeta {
  const out = {} as StatusMeta;
  for (const key of Object.keys(STATUS_META) as AppointmentStatus[]) {
    const base = STATUS_META[key];
    const over = overrides?.[key];
    out[key] = {
      tone: base.tone,
      label: over?.label?.trim() || base.label,
      icon: over?.icon?.trim() || base.icon,
      color: over?.color && HEX.test(over.color.trim()) ? over.color.trim() : STATUS_COLOR[key],
    };
  }
  return out;
}

const Ctx = createContext<StatusMeta>(mergeStatusMeta(undefined));

/**
 * Reads the clinic's statuses once and hands them to every screen that draws one.
 *
 * From `GET /settings/look`, not from the full settings: those need
 * `setari.vede`, which reception, doctors and assistants do not hold — the
 * people who work in the calendar all day. They used to get a 403 and see the
 * standard names and colours instead of the clinic's, on the same booking the
 * administrator saw differently.
 */
export function StatusMetaProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const q = useQuery({
    queryKey: ['settings', 'look'],
    queryFn: settingsApi.look,
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  });
  const overrides = q.data?.statusLabels as StatusOverrides | undefined;
  const value = useMemo(() => mergeStatusMeta(overrides), [overrides]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The statuses as this clinic words them; the defaults where it has not. */
export function useStatusMeta(): StatusMeta {
  return useContext(Ctx);
}
